import React, {
  FC, useState, ReactNode,
} from 'react';
import useOnclickOutside from 'react-cool-onclickoutside';
import useKeyDown from '../../hooks/useKeyDown';

type DropdownChildrenProps = {
  toggle: () => void;
  isOpen: boolean;
  open: () => void;
  close: () => void;
};

type DropdownProps = {
  children: (props: DropdownChildrenProps) => ReactNode;
  className?: string;
  disableOnClickOutside?: boolean;
  initiallyOpen?: boolean;
  /**
   * This tells `react-cool-onclickoutside` to ignore certain elements during the event loop
   */
  ignoreClass?: string;
  'data-test'?: string;
};

const Dropdown: FC<DropdownProps> = ({
  children,
  className = '',
  // Note: this means that the prop is called
  // "disableOnClickOutside",
  // but it's called "disabled" within this component.
  disableOnClickOutside: disabled = false,
  initiallyOpen = false,
  ignoreClass,
  'data-test': dataTest,
}) => {
  const [openState, setOpen] = useState(initiallyOpen);
  const ref = useOnclickOutside(
    () => setOpen(false),
    { disabled, ignoreClass },
  );

  const toggle = () => setOpen((open) => !open);
  const open = () => setOpen(true);
  const close = () => setOpen(false);

  useKeyDown('Escape', close);

  return (
    <div
      data-test={dataTest}
      className={className}
      ref={ref}
    >
      {/* This looks wonky, but it basically means "call the children function
          as a component with the given props */}
      {children({
        toggle,
        isOpen: openState,
        open,
        close,
      })}
    </div>
  );
};

export default Dropdown;
